// ============================================================
// Shared printable-report window helper
// ============================================================
// Replaces the legacy `window.open("") + document.write(html)` flow
// for the Symptom Intelligence Report (SIR) and the Annual Wellness
// Summary (AWS). Behaviour:
//
//   1. prepareReportWindow() is called SYNCHRONOUSLY from a click
//      handler so the popup-blocker gesture exemption is preserved
//      across the awaits used while building the report. It returns
//      a Window handle, or null if the browser blocked it (common
//      inside Instagram/Facebook/Gmail in-app browsers).
//
//   2. finalizeReportWindow(win, html, title) injects the title into
//      the document <head>, turns the HTML into a Blob URL, and
//      navigates the pre-opened tab to that URL. If `win` is null
//      (popup blocked), it renders a visible in-app fallback overlay
//      with a clickable "Open your report" link to the same Blob URL.
//
// The Blob URL is revoked on a delay so the new tab has time to load
// the document (and the fallback link remains clickable for a while).
// ============================================================

const FALLBACK_OVERLAY_ID = "knowmeno-report-popup-fallback";

function ensureTitle(html: string, title: string): string {
  // If the document already has a <title>, leave it alone — the existing
  // one already encodes the date/name. Otherwise inject one.
  if (/<title>[\s\S]*?<\/title>/i.test(html)) return html;
  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/<head([^>]*)>/i, `<head$1><title>${escapeForTitle(title)}</title>`);
  }
  // No <head> at all — wrap defensively.
  return html.replace(/<html([^>]*)>/i, `<html$1><head><title>${escapeForTitle(title)}</title></head>`);
}

function escapeForTitle(s: string): string {
  return String(s).replace(/[<>&"']/g, (c) =>
    ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;", "'": "&#39;" }[c] as string),
  );
}

/**
 * Open a blank tab synchronously inside the user-gesture handler so it
 * survives later `await`s. Returns null if the browser blocked it.
 */
export function prepareReportWindow(): Window | null {
  try {
    return window.open("", "_blank", "width=800,height=900");
  } catch {
    return null;
  }
}

/**
 * Navigate the pre-opened window to a Blob URL containing the printable
 * report HTML. If the window is null (popup blocker / in-app browser),
 * render a visible in-app fallback link to the same Blob URL instead of
 * failing silently.
 */
export function finalizeReportWindow(
  win: Window | null,
  html: string,
  title: string,
): void {
  const finalHtml = ensureTitle(html, title);
  let blobUrl: string;
  try {
    const blob = new Blob([finalHtml], { type: "text/html;charset=utf-8" });
    blobUrl = URL.createObjectURL(blob);
  } catch (e) {
    console.error("[report] failed to build Blob URL", e);
    return;
  }

  if (win && !win.closed) {
    try {
      // Navigate the pre-opened tab to the Blob URL. The tab title comes
      // from the <title> inside the document itself.
      win.location.replace(blobUrl);
      try { win.focus(); } catch {}
    } catch (e) {
      console.error("[report] failed to navigate report window", e);
      showFallback(blobUrl, title);
    }
    // Revoke after enough time for the new tab to load + the user to
    // trigger Print/Save-as-PDF (which keeps the resource alive while
    // open).
    setTimeout(() => { try { URL.revokeObjectURL(blobUrl); } catch {} }, 60_000);
    return;
  }

  // Popup blocked / in-app browser — show in-app fallback link.
  showFallback(blobUrl, title);
}

function showFallback(blobUrl: string, title: string): void {
  try {
    // Remove any previous fallback so consecutive attempts don't stack.
    const existing = document.getElementById(FALLBACK_OVERLAY_ID);
    if (existing) existing.remove();

    const overlay = document.createElement("div");
    overlay.id = FALLBACK_OVERLAY_ID;
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.style.cssText = [
      "position:fixed", "inset:0", "z-index:2147483647",
      "background:rgba(43,43,40,0.55)",
      "display:flex", "align-items:center", "justify-content:center",
      "padding:24px",
      "font-family:-apple-system,system-ui,sans-serif",
    ].join(";");

    const card = document.createElement("div");
    card.style.cssText = [
      "background:#fff", "border-radius:18px",
      "padding:24px 22px", "max-width:380px", "width:100%",
      "box-shadow:0 20px 60px rgba(0,0,0,0.25)",
      "color:#2b2b28",
    ].join(";");

    const h = document.createElement("div");
    h.textContent = "Your report is ready";
    h.style.cssText = "font-size:17px;font-weight:600;margin-bottom:6px;color:#4B1F4F;";

    const p = document.createElement("div");
    p.textContent = "Your browser blocked the automatic tab. Tap below to open it.";
    p.style.cssText = "font-size:13px;line-height:1.55;color:#6b6a64;margin-bottom:16px;";

    const link = document.createElement("a");
    link.href = blobUrl;
    link.target = "_blank";
    link.rel = "noopener";
    link.textContent = "Open your report →";
    link.style.cssText = [
      "display:block", "text-align:center",
      "padding:13px 18px", "border-radius:12px",
      "background:#4B1F4F", "color:#fff",
      "text-decoration:none", "font-size:14px", "font-weight:600",
      "margin-bottom:10px",
    ].join(";");
    link.title = title;

    const close = document.createElement("button");
    close.type = "button";
    close.textContent = "Close";
    close.style.cssText = [
      "display:block", "width:100%",
      "padding:10px", "border-radius:10px",
      "background:transparent", "border:1px solid #e8e6df",
      "color:#6b6a64", "font-size:13px", "cursor:pointer",
    ].join(";");
    close.onclick = () => {
      try { overlay.remove(); } catch {}
      // Best-effort revoke when user dismisses without clicking the link.
      setTimeout(() => { try { URL.revokeObjectURL(blobUrl); } catch {} }, 5_000);
    };

    card.appendChild(h);
    card.appendChild(p);
    card.appendChild(link);
    card.appendChild(close);
    overlay.appendChild(card);
    document.body.appendChild(overlay);

    // If the user clicks through to the link, give the new tab time to load
    // before revoking. (Mobile in-app browsers can be slow.)
    link.addEventListener("click", () => {
      setTimeout(() => { try { URL.revokeObjectURL(blobUrl); } catch {} }, 120_000);
    });
  } catch (e) {
    console.error("[report] failed to render fallback overlay", e);
    // Last resort: hand the URL to the user via prompt so they can copy it.
    try { window.prompt("Copy this link to open your report:", blobUrl); } catch {}
  }
}
